import type { MerchantTableBillingRule, RListMerchantTableBillingRule } from "@repo/codegen/teashop-merchant-api"
import type { AxiosResponse } from "axios"
import dayjs from "dayjs"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import layoutSettings from "@/config/layout"
import { useUserStore } from "@/store/modules/user"
import {
  getIsPrintCheckedBillLocalStorage,
  getIsPrintRechargeBillLocalStorage,
  setIsPrintCheckedBillLocalStorage,
  setIsPrintRechargeBillLocalStorage,
} from "@/utils/cache/localStorage"
import { getCurrentDate } from "@/utils/index"

export const useSettingsStore = defineStore("settings", () => {
  const userStore = useUserStore()
  const shopId = userStore.shopId
  const fixedHeader = ref<boolean>(layoutSettings.fixedHeader)
  const showSettings = ref<boolean>(layoutSettings.showSettings)
  const showTagsView = ref<boolean>(layoutSettings.showTagsView)
  const showSidebarLogo = ref<boolean>(layoutSettings.showSidebarLogo)
  const showNotify = ref<boolean>(layoutSettings.showNotify)
  const showThemeSwitch = ref<boolean>(layoutSettings.showThemeSwitch)
  const showScreenfull = ref<boolean>(layoutSettings.showScreenfull)
  const showGreyMode = ref<boolean>(layoutSettings.showGreyMode)
  const showColorWeakness = ref<boolean>(layoutSettings.showColorWeakness)
  const bussinessDate = ref<number>()
  const businessDateShopId = ref<number>()
  const businessDateReady = computed(() =>
    userStore.shopId > 0 && businessDateShopId.value === userStore.shopId && bussinessDate.value !== undefined,
  )

  /**
   * 桌台计费规则
   */
  const tableRuleList = ref<MerchantTableBillingRule[]>([])

  const getTableRuleList = () => computed(() => tableRuleList.value)

  // TODO 本机器或者其他机器修改计费规则等时，需要刷新
  const fetchTableRuleListPromise = ref<Promise<AxiosResponse<RListMerchantTableBillingRule, any>>>(new Promise(() => { }))

  const fetchTableRuleList = async () => {
    // settings 由请求层间接导入，聚合 API 必须在请求层初始化完成后按需加载。
    fetchTableRuleListPromise.value = import("@/api/teashop-merchant").then(({ merchantTableBillingRuleAPI }) =>
      merchantTableBillingRuleAPI.merchantTableBillingRuleGetList({ shopId }),
    )
    const res = await fetchTableRuleListPromise.value
    tableRuleList.value = res.data?.data ?? []
  }

  const payMethodInfo: any = ref([])
  const setPayMethodInfo = (value: any) => {
    payMethodInfo.value = value
  }

  const clearBusinessDate = () => {
    bussinessDate.value = undefined
    businessDateShopId.value = undefined
  }

  const getBusinessDateMinutes = () => businessDateReady.value ? bussinessDate.value : undefined

  const setBussinessDate = (val: number, targetShopId = userStore.shopId) => {
    if (targetShopId !== userStore.shopId || targetShopId <= 0) return
    if (!Number.isInteger(val) || val < 0 || val >= 24 * 60) return
    businessDateShopId.value = targetShopId
    bussinessDate.value = val
  }

  const getBussinessDate = () => {
    if (businessDateReady.value) {
      const time =
        Math.floor(Number(bussinessDate.value) / 60)
          .toString()
          .padStart(2, "0") +
        ":" +
        (Number(bussinessDate.value) % 60).toString().padStart(2, "0")
      return time + ":00"
    } else {
      return "00:00:00"
    }
  }

  const getDeafultDate: (bussinessDate: string) => [string, string] = (bussinessDate: string) => {
    const [hours, minutes, seconds] = bussinessDate.split(":").map(Number)
    const date = dayjs(getCurrentDate()).hour(hours).minute(minutes).second(seconds).millisecond(0).toDate()
    const nowDate = new Date()
    const sevenDate = new Date()
    const oneDay = 1000 * 60 * 60 * 24
    const sevenDay = 6000 * 60 * 60 * 24
    if (date > nowDate) {
      nowDate.setTime(nowDate.getTime() - oneDay)
      nowDate.setHours(0, 0, 0, 0) // 分别设置小时、分钟、秒、毫秒为0

      sevenDate.setTime(nowDate.getTime() - sevenDay)
      sevenDate.setHours(0, 0, 0, 0)
      return [dayjs(sevenDate).format("YYYY-MM-DD"), dayjs(nowDate).format("YYYY-MM-DD")]
    }

    sevenDate.setTime(nowDate.getTime() - sevenDay)
    sevenDate.setHours(0, 0, 0, 0)
    return [dayjs(sevenDate).format("YYYY-MM-DD"), dayjs(new Date()).format("YYYY-MM-DD")]
  }

  /**
   * 充值单打印、营业单打印的默认开启状态
   */
  const isPrintRechargeBill = ref<boolean>(getIsPrintRechargeBillLocalStorage() === "true")
  const isPrintCheckedBill = ref<boolean>(getIsPrintCheckedBillLocalStorage() === "true")

  const setIsPrintRechargeBill = (value: boolean) => {
    isPrintRechargeBill.value = value
    setIsPrintRechargeBillLocalStorage(value)
  }

  const setIsPrintCheckedBill = (value: boolean) => {
    isPrintCheckedBill.value = value
    setIsPrintCheckedBillLocalStorage(value)
  }

  const getIsPrintRechargeBill = () => isPrintRechargeBill.value
  const getIsPrintCheckedBill = () => isPrintCheckedBill.value

  return {
    fixedHeader,
    showSettings,
    showTagsView,
    showSidebarLogo,
    showNotify,
    showThemeSwitch,
    showScreenfull,
    showGreyMode,
    showColorWeakness,
    payMethodInfo,
    setPayMethodInfo,
    setBussinessDate,
    clearBusinessDate,
    getBusinessDateMinutes,
    businessDateReady,
    getBussinessDate,
    getDeafultDate,
    fetchTableRuleList,
    fetchTableRuleListPromise,
    getTableRuleList,
    setIsPrintRechargeBill,
    setIsPrintCheckedBill,
    getIsPrintRechargeBill,
    getIsPrintCheckedBill,
  }
})
